import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../AuthContext';
import {
    Card,
    CardBody,
    Typography,
    Chip,
    Button,
    Alert
} from '@material-tailwind/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSyncAlt, faExclamationTriangle, faTrash } from '@fortawesome/free-solid-svg-icons';
const { rootUrl } = require('../../../config/config');

const RemittanceSummary = ({ weekKey, plateNo }) => {
    const { token } = useAuth();
    const authToken = token || localStorage.getItem('authToken') || 'default-token';

    const [summary, setSummary] = useState([]);
    const [loading, setLoading] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [remittanceToDelete, setRemittanceToDelete] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    useEffect(() => {
        if (weekKey) {
            fetchSummary();
        }
    }, [weekKey, plateNo]);

    const fetchSummary = async () => {
        setLoading(true);
        try {
            const params = {};
            if (plateNo) params.plate_no = plateNo;

            const response = await axios.get(
                `${rootUrl}/api/remittance/summary/${weekKey}`,
                {
                    headers: { 'Authorization': `Bearer ${authToken}` },
                    params
                }
            );

            if (response.data && response.data.success) {
                setSummary(response.data.data || []);
            } else {
                setSummary(response.data || []);
            }
        } catch (error) {
            console.error('Error fetching summary:', error);
            setSummary([]);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString();
    };

    const formatCurrency = (amount) => {
        if (!amount) return '0.00';
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES'
        }).format(amount);
    };

    const getStatusColor = (status) => {
        const colors = {
            'PENDING': 'yellow',
            'PROCESSED': 'blue',
            'PAID': 'green'
        };
        return colors[status] || 'gray';
    };

    const handleDeleteClick = (record) => {
        setRemittanceToDelete(record);
        setDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!remittanceToDelete) return;

        setDeleteLoading(true);
        try {
            const response = await axios.delete(
                `${rootUrl}/api/remittance/${remittanceToDelete.id}`,
                {
                    headers: { 'Authorization': `Bearer ${authToken}` }
                }
            );

            if (response.data && response.data.success) {
                setSuccess('Remittance deleted successfully');
                setDeleteModalOpen(false);
                setRemittanceToDelete(null);
                fetchSummary(); // Refresh the list
                setTimeout(() => setSuccess(null), 3000);
            } else {
                setError('Error: ' + (response.data?.message || 'Failed to delete remittance'));
            }
        } catch (error) {
            console.error('Error deleting remittance:', error);
            setError('Error deleting remittance: ' + (error.response?.data?.message || error.message));
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleCancelDelete = () => {
        setDeleteModalOpen(false);
        setRemittanceToDelete(null);
    };

    return (
        <div className="space-y-4">
            {error && (
                <Alert
                    color="red"
                    icon={<FontAwesomeIcon icon={faExclamationTriangle} />}
                    onClose={() => setError(null)}
                >
                    {error}
                </Alert>
            )}

            {success && (
                <Alert
                    color="green"
                    icon={<FontAwesomeIcon icon={faSyncAlt} />}
                    onClose={() => setSuccess(null)}
                >
                    {success}
                </Alert>
            )}

            <div className="flex justify-between items-center">
                <Typography variant="h6">Remittance Summary for Week: {weekKey}</Typography>
                <Button
                    size="sm"
                    color="blue"
                    onClick={fetchSummary}
                >
                    <FontAwesomeIcon icon={faSyncAlt} className="mr-2" />
                    Refresh
                </Button>
            </div>

            {loading ? (
                <Card>
                    <CardBody className="text-center py-8">
                        <Typography>Loading summary...</Typography>
                    </CardBody>
                </Card>
            ) : summary.length === 0 ? (
                <Card>
                    <CardBody className="text-center py-8">
                        <FontAwesomeIcon icon={faExclamationTriangle} size="2x" className="text-gray-400 mb-2" />
                        <Typography variant="paragraph" className="text-gray-600">
                            No remittance records found for this week
                        </Typography>
                    </CardBody>
                </Card>
            ) : (
                <div className="space-y-4">
                    {summary.map((record) => (
                        <Card key={record.id}>
                            <CardBody>
                                <div className="flex justify-between items-start mb-4">
                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 flex-1">
                                        <div>
                                            <Typography variant="small" color="gray">Plate Number</Typography>
                                            <Typography variant="h6">{record.plate_no}</Typography>
                                        </div>
                                        <div>
                                            <Typography variant="small" color="gray">Owner</Typography>
                                            <Typography variant="h6">{record.owner_name || record.owner_code}</Typography>
                                        </div>
                                        <div>
                                            <Typography variant="small" color="gray">Status</Typography>
                                            <Chip
                                                value={record.status}
                                                color={getStatusColor(record.status)}
                                                className="mt-1"
                                            />
                                        </div>
                                        <div>
                                            <Typography variant="small" color="gray">Processing Date</Typography>
                                            <Typography variant="h6">{formatDate(record.processing_date)}</Typography>
                                        </div>
                                    </div>
                                    {(record.status === 'PENDING' || record.status === 'PROCESSED') && (
                                        <Button
                                            size="sm"
                                            color="red"
                                            variant="outlined"
                                            onClick={() => handleDeleteClick(record)}
                                            className="flex items-center gap-2"
                                        >
                                            <FontAwesomeIcon icon={faTrash} />
                                            Delete
                                        </Button>
                                    )}
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t">
                                    <Card className="bg-green-50">
                                        <CardBody>
                                            <Typography variant="small" color="gray">Total Remittance</Typography>
                                            <Typography variant="h5" className="text-green-600">
                                                {formatCurrency(record.total_remittance)}
                                            </Typography>
                                        </CardBody>
                                    </Card>
                                    <Card className="bg-red-50">
                                        <CardBody>
                                            <Typography variant="small" color="gray">Total Charges</Typography>
                                            <Typography variant="h5" className="text-red-600">
                                                {formatCurrency(record.total_charges)}
                                            </Typography>
                                        </CardBody>
                                    </Card>
                                    <Card className="bg-blue-50">
                                        <CardBody>
                                            <Typography variant="small" color="gray">Owner Payout</Typography>
                                            <Typography variant="h5" className="text-blue-600">
                                                {formatCurrency(record.owner_payout)}
                                            </Typography>
                                        </CardBody>
                                    </Card>
                                </div>

                                {record.applied_charges && record.applied_charges.length > 0 && (
                                    <div className="mt-4 pt-4 border-t">
                                        <Typography variant="small" color="gray" className="mb-2">
                                            Applied Charges ({record.applied_charges.length}):
                                        </Typography>
                                        <div className="space-y-1">
                                            {record.applied_charges.map((charge, index) => (
                                                <div key={index} className="flex justify-between text-sm">
                                                    <span>{charge.description || charge.charge_type}</span>
                                                    <span className="font-semibold">{formatCurrency(charge.charge_amount)}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </CardBody>
                        </Card>
                    ))}
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {deleteModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <Card className="w-full max-w-md mx-4">
                        <CardBody className="space-y-4">
                            <div className="flex items-center gap-3 text-red-600">
                                <FontAwesomeIcon icon={faTrash} size="lg" />
                                <Typography variant="h5">Delete Remittance</Typography>
                            </div>

                            <Typography variant="paragraph" className="text-gray-700">
                                Are you sure you want to delete this remittance?
                            </Typography>

                            {remittanceToDelete && (
                                <div className="bg-gray-50 p-3 rounded space-y-1 text-sm">
                                    <div><strong>Plate:</strong> {remittanceToDelete.plate_no}</div>
                                    <div><strong>Week:</strong> {weekKey}</div>
                                    <div><strong>Owner:</strong> {remittanceToDelete.owner_name || remittanceToDelete.owner_code}</div>
                                    <div><strong>Status:</strong> {remittanceToDelete.status}</div>
                                    <div><strong>Payout:</strong> {formatCurrency(remittanceToDelete.owner_payout)}</div>
                                </div>
                            )}

                            <Typography variant="small" className="text-red-600">
                                <strong>Note:</strong> This action will archive the remittance and all applied charges.
                                Charges will be marked as pending again. This action cannot be undone.
                            </Typography>

                            <div className="flex gap-3 justify-end pt-2">
                                <Button
                                    size="sm"
                                    variant="outlined"
                                    onClick={handleCancelDelete}
                                    disabled={deleteLoading}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    size="sm"
                                    color="red"
                                    onClick={handleConfirmDelete}
                                    disabled={deleteLoading}
                                >
                                    {deleteLoading ? 'Deleting...' : 'Delete'}
                                </Button>
                            </div>
                        </CardBody>
                    </Card>
                </div>
            )}
        </div>
    );
};

export default RemittanceSummary;

